import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  AutomatedTrafficFineNotificationVehiclesDelete,
  AutomatedTrafficFineNotificationVehiclesNotify,
  AutomatedTrafficFineNotificationVehiclesSelected
} from '../../../../assets/icons'
import { VIHCLE_TYPES_STATE } from '../../../../constants/global'
import './index.scss'

const SWIPE_OPEN_WIDTH = 62
const OPEN_THRESHOLD = 50
const TAP_SLOP = 6

const getVehicleTypeInfo = (vehicleType) => {
  // Map từ vehicleType trong data API sang VIHCLE_TYPES_STATE
  if (vehicleType === 1) return VIHCLE_TYPES_STATE.CAR // Ô tô
  if (vehicleType === 30) return VIHCLE_TYPES_STATE.MOTO_BIKE // Xe máy
  if (vehicleType === 40) return VIHCLE_TYPES_STATE.ELECTRIC // Xe máy điện
  return VIHCLE_TYPES_STATE.CAR // Default
}

export default function AutomatedTrafficFineNotificationVehicleItem({ vehicle, selected, disabled, onSelect, onDelete }) {
  const [offset, setOffset] = useState(0)
  const [open, setOpen] = useState(false)
  const start = useRef({ x: 0, y: 0, base: 0, dragging: false, moved: false })

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v))
  const close = () => {
    setOffset(0)
    setOpen(false)
  }

  // Mouse event handlers
  const handleMouseDown = (e) => {
    start.current = { x: e.clientX, y: e.clientY, base: open ? -SWIPE_OPEN_WIDTH : 0, dragging: true, moved: false }
  }

  const handleMouseMove = (e) => {
    if (!start.current.dragging) return
    const dx = e.clientX - start.current.x,
      dy = e.clientY - start.current.y
    if (!start.current.moved) {
      if (Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP) return
      if (Math.abs(dy) > 30 && Math.abs(dy) > Math.abs(dx) * 1.5) {
        start.current.dragging = false
        start.current.moved = true
        return
      }
      start.current.moved = true
    }
    setOffset(clamp(start.current.base + dx, -SWIPE_OPEN_WIDTH * 1.3, 0))
  }

  const handleMouseUp = () => {
    const wasDragging = start.current.moved
    start.current.dragging = false
    if (Math.abs(offset) > OPEN_THRESHOLD) {
      setOffset(-SWIPE_OPEN_WIDTH)
      setOpen(true)
    } else close()
    if (!wasDragging) {
      if (open) close()
      else if (!disabled) onSelect?.(vehicle)
    }
  }

  const handleMouseLeave = () => {
    if (start.current.dragging) {
      start.current.dragging = false
      if (Math.abs(offset) > OPEN_THRESHOLD) {
        setOffset(-SWIPE_OPEN_WIDTH)
        setOpen(true)
      } else {
        close()
      }
    }
  }

  // Touch event handlers
  const handleTouchStart = (e) => {
    const touch = e.touches[0]
    start.current = { x: touch.clientX, y: touch.clientY, base: open ? -SWIPE_OPEN_WIDTH : 0, dragging: true, moved: false }
  }

  const handleTouchMove = (e) => {
    if (!start.current.dragging) return
    const touch = e.touches[0]
    const dx = touch.clientX - start.current.x,
      dy = touch.clientY - start.current.y
    if (!start.current.moved) {
      if (Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP) return
      if (Math.abs(dy) > 30 && Math.abs(dy) > Math.abs(dx) * 1.5) {
        start.current.dragging = false
        start.current.moved = true
        return
      }
      start.current.moved = true
    }
    setOffset(clamp(start.current.base + dx, -SWIPE_OPEN_WIDTH * 1.3, 0))
  }

  const handleTouchEnd = (e) => {
    if (e.cancelable) e.preventDefault()
    const wasDragging = start.current.moved
    start.current.dragging = false
    if (Math.abs(offset) > OPEN_THRESHOLD) {
      setOffset(-SWIPE_OPEN_WIDTH)
      setOpen(true)
    } else close()
    if (!wasDragging) {
      if (open) close()
      else if (!disabled) onSelect?.(vehicle)
    }
  }

  const handleTouchCancel = () => {
    start.current.dragging = false
    close()
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    close()
    onDelete(vehicle.id)
  }

  if (!vehicle) return null

  const typeInfo = getVehicleTypeInfo(vehicle.vehicleType)

  return (
    <div className="vehicle-item-wrapper">
      <div className={`vehicle-item ${selected ? 'selected' : ''} ${disabled ? 'is-disabled' : ''}`}>
        <div
          className="vehicle-item__delete-container"
          style={{
            opacity: offset < -10 ? 1 : 0,
            pointerEvents: 'auto'
          }}
          onClick={handleDelete}>
          <div className="vehicle-item__delete" role="button" aria-label="Xóa phương tiện">
            <AutomatedTrafficFineNotificationVehiclesDelete className="vehicle-item__delete-icon" />
            <div className="vehicle-item__delete-text">Xóa</div>
          </div>
        </div>
        <div
          className="vehicle-item__overlay"
          style={{ transform: `translateX(${offset}px)`, transition: start.current.dragging ? 'none' : 'transform 160ms ease' }}
        />
        <div
          className={`vehicle-item__content ${offset < 0 ? 'dragging' : ''}`}
          style={{ transform: `translateX(${offset}px)`, transition: start.current.dragging ? 'none' : 'transform 160ms ease' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}>
          {selected && (
            <div className={`vehicle-item__check ${offset < 0 ? 'hidden' : ''}`}>
              <AutomatedTrafficFineNotificationVehiclesSelected className="vehicle-item__check-icon" />
            </div>
          )}
          <div className="vehicle-item__left">{typeInfo.icon}</div>
          <div className="vehicle-item__body">
            <div className="vehicle-item__plate">{vehicle.plate}</div>
            <div className="vehicle-item__type">{typeInfo.label}</div>
          </div>
          {vehicle?.activeNotifyPackageIndex >= 0 && vehicle?.customerSchedule?.[vehicle.activeNotifyPackageIndex] && (
            <div className="vehicle-item__package-info">
              <AutomatedTrafficFineNotificationVehiclesNotify />
              <span>{vehicle?.customerSchedule?.[vehicle.activeNotifyPackageIndex]?.productName}</span>
            </div>
          )}
          {offset < 0 && <div className="vehicle-item__arrow">›</div>}
        </div>
      </div>
    </div>
  )
}

AutomatedTrafficFineNotificationVehicleItem.propTypes = {
  vehicle: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    plate: PropTypes.string.isRequired,
    vehicleType: PropTypes.number.isRequired
  }).isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  onDelete: PropTypes.func
}

AutomatedTrafficFineNotificationVehicleItem.defaultProps = {
  selected: false,
  disabled: false,
  onSelect: null,
  onDelete: null
}
